import { gql, useQuery } from '@apollo/client';
import { Heading } from '@apollo/orbit';
import moment from 'moment';
import React, { useCallback } from 'react';
import { useHistory, useLocation } from 'react-router';

import { useIsAppInBackgroundContext } from 'src/app/appLayout/hooks/useIsAppInBackground';
import { insightsRouteConfig } from 'src/app/graph/routes';
import { useRouteParams } from 'src/hooks/useRouteParams';
import Config from 'src/lib/config';
import { date } from 'src/lib/date/date';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import {
  OperationsOverviewQuery,
  OperationsOverviewQueryVariables,
} from 'src/lib/graphqlTypes/types';
import { useLDFlag } from 'src/lib/launchDarkly';
import {
  NormalizedOperationsQueryStats,
  aggregateStats,
  calculateMetrics,
} from 'src/lib/metrics';
import { mergeQueryParams } from 'src/lib/routing';

import {
  OVERVIEW_METRICS,
  OVERVIEW_METRIC_KEYS,
  OperationOverviewMetadata,
  OverviewCard,
  cardSubtitles,
  cardTitles,
} from './OverviewCard';
import { OverviewCardLoadingProps, OverviewLoading } from './OverviewLoading';

// This could be moved to a dynamic number using react state but
// for now will keep as it is until we have product input on how
// we want users to select different ranges.
const SHOW_TOP_N = 5;

const TopNOperationDetailsFragment = gql`
  fragment TopNOperationDetailsFragment on TopNOperationsRecord {
    id: queryID
    displayName
    type
  }
`;

export const operationsOverviewQuery = gql<
  OperationsOverviewQuery,
  OperationsOverviewQueryVariables
>`
  query OperationsOverviewQuery(
    $serviceId: ID!
    $graphVariant: String!
    $timeFrom: Timestamp!
    $timeTo: Timestamp!
    $compareToTimeFrom: Timestamp!
    $compareToTimeTo: Timestamp!
    $includeDeltaValues: Boolean!
    $topN: Int!
    $resolution: Resolution!
    $queryStatsFilter: ServiceQueryStatsFilter
    $insightsListFilter: OperationInsightsListFilterInput
    $topNFilter: TopNOperationsFilterInput
    $useOptimizedQuery: Boolean!
  ) {
    service(id: $serviceId) {
      id
      sparklines: statsWindow(
        from: $timeFrom
        to: $timeTo
        resolution: $resolution
      ) {
        queryStats(filter: $queryStatsFilter) {
          timestamp
          metrics {
            totalLatencyHistogram {
              totalCount
              serviceTimeP95: durationMs(percentile: 0.95)
            }
            requestsWithErrorsCount
          }
        }
      }

      # We can infer the total request count and requests with errors count from
      # the sparklines query
      currentStats: statsWindow(from: $timeFrom, to: $timeTo) {
        queryStats(filter: $queryStatsFilter) {
          metrics {
            totalLatencyHistogram {
              totalCount @skip(if: $useOptimizedQuery)
              serviceTimeP95: durationMs(percentile: 0.95)
            }
            requestsWithErrorsCount @skip(if: $useOptimizedQuery)
          }
        }
      }

      pastStats: statsWindow(from: $compareToTimeFrom, to: $compareToTimeTo)
        @include(if: $includeDeltaValues) {
        queryStats(filter: $queryStatsFilter) {
          metrics {
            totalLatencyHistogram {
              totalCount
              serviceTimeP95: durationMs(percentile: 0.95)
            }
            requestsWithErrorsCount
          }
        }
      }

      variant(name: $graphVariant) {
        id

        byRequests: operationInsightsList(
          first: $topN
          from: $timeFrom
          to: $timeTo
          filter: $insightsListFilter
          orderBy: { column: REQUEST_COUNT_PER_MIN, direction: DESCENDING }
        ) @skip(if: $useOptimizedQuery) {
          operations: nodes {
            id
            displayName
            type
            requestCountPerMin
          }
        }

        byError: operationInsightsList(
          first: $topN
          from: $timeFrom
          to: $timeTo
          filter: $insightsListFilter
          orderBy: { column: ERROR_PERCENTAGE, direction: DESCENDING }
        ) @skip(if: $useOptimizedQuery) {
          operations: nodes {
            id
            displayName
            type
            errorPercentage
          }
        }

        byP95: operationInsightsList(
          first: $topN
          from: $timeFrom
          to: $timeTo
          filter: $insightsListFilter
          orderBy: { column: SERVICE_TIME_P95, direction: DESCENDING }
        ) @skip(if: $useOptimizedQuery) {
          operations: nodes {
            id
            displayName
            type
            serviceTimeP95Ms
          }
        }

        topNOperations(
          topN: $topN
          from: $timeFrom
          to: $timeTo
          filter: $topNFilter
        ) @include(if: $useOptimizedQuery) {
          byRequestRate {
            ...TopNOperationDetailsFragment
            requestCountPerMin
          }
          byErrorPercentage {
            ...TopNOperationDetailsFragment
            errorPercentage
          }
          byP95 {
            ...TopNOperationDetailsFragment
            serviceTimeP95Ms
          }
        }
      }
    }
  }

  ${TopNOperationDetailsFragment}
`;

function getTopNOperationStats(
  useOptimizedQuery: boolean,
  data: OperationsOverviewQuery,
): Record<
  keyof typeof OVERVIEW_METRICS,
  OperationOverviewMetadata[] | undefined
> {
  const variantData = data?.service?.variant;
  const byRequestRate = useOptimizedQuery
    ? variantData?.topNOperations?.byRequestRate
    : variantData?.byRequests?.operations;
  const byErrorPercentage = useOptimizedQuery
    ? variantData?.topNOperations?.byErrorPercentage
    : variantData?.byError?.operations;
  const byP95 = useOptimizedQuery
    ? variantData?.topNOperations?.byP95
    : variantData?.byP95?.operations;

  return {
    requestRate: byRequestRate?.map((operation) => ({
      id: operation.id,
      name: operation.displayName,
      type: operation.type,
      requestRate: operation.requestCountPerMin,
      errorPercentage: null,
      serviceTimeP95: null,
    })),
    errorPercentage: byErrorPercentage?.map((operation) => ({
      id: operation.id,
      name: operation.displayName,
      type: operation.type,
      requestRate: null,
      errorPercentage: operation.errorPercentage,
      serviceTimeP95: null,
    })),
    serviceTimeP95: byP95?.map((operation) => ({
      id: operation.id,
      name: operation.displayName,
      type: operation.type,
      requestRate: null,
      errorPercentage: null,
      serviceTimeP95: operation.serviceTimeP95Ms,
    })),
  };
}

const overviewLoadingCards: OverviewCardLoadingProps[] = [
  {
    title: cardTitles.requestRate,
    subtitle: cardSubtitles.requestRate,
  },
  {
    title: cardTitles.serviceTimeP95,
    subtitle: cardSubtitles.serviceTimeP95,
  },
  {
    title: cardTitles.errorPercentage,
    subtitle: cardSubtitles.errorPercentage,
  },
];

export function OverviewSection({
  serviceId,
  filter,
}: {
  serviceId: string;
  filter:
    | GraphQLTypes.ServiceQueryStatsFilter
    | GraphQLTypes.ServiceErrorStatsFilter
    | GraphQLTypes.ServiceTracePathErrorsRefsFilter
    | GraphQLTypes.ServiceTraceRefsFilter;
}) {
  const {
    to,
    from,
    range: timeRange,
    tab,
    graphVariant,
    fastModeRange,
  } = useRouteParams(insightsRouteConfig);
  const useOptimizedQuery = useLDFlag(
    'insights-use-new-operation-overview-druid-queries',
  );

  const range = timeRange ?? 'lastDay';
  const isInBackground = useIsAppInBackgroundContext();
  const { timeFrom, timeTo, resolution, pollInterval } = date.getDateBounds(
    range,
    { from, to },
    undefined,
    fastModeRange,
  );

  const compareToTimeFrom = `-${moment.duration(2, 'days').asSeconds()}`;
  const compareToTimeTo = timeFrom;

  // We only show the delta values if the user is viewing the "lastDay" time range.
  // Otherwise, it leads to more edge cases to handle with
  // respect to what time ranges are allowed for each plan and also to
  // questions about how to compute a delta over an arbitrary time range.
  const includeDeltaValues = range === 'lastDay';

  const clientFilter = {
    or: filter.or?.map((or) => ({
      clientName: or.clientName,
      clientVersion: or.clientVersion,
    })),
  };

  const overviewQueryVars = {
    serviceId,
    timeFrom,
    timeTo,
    queryStatsFilter: filter,
    graphVariant,
    compareToTimeFrom,
    compareToTimeTo,
    includeDeltaValues,
    topN: SHOW_TOP_N,
    resolution,
    topNFilter: clientFilter,
    insightsListFilter: clientFilter,
    useOptimizedQuery,
  };

  const { data, loading } = useQuery(operationsOverviewQuery, {
    variables: overviewQueryVars,
    skip: isInBackground,
    pollInterval,
    skipPollAttempt: useCallback(() => isInBackground, [isInBackground]),
  });

  const history = useHistory();
  const location = useLocation();
  const onQuerySelected = useCallback(
    (
      { id, name }: { id: string | null; name: string | null },
      metric: string,
    ) => {
      const newTabParam =
        !id && (tab === 'operation' || tab === 'traces') ? null : tab;
      history.push({
        ...location,
        search: mergeQueryParams(location.search, {
          [Config.queryParameters.SelectedQueryId]: id,
          [Config.queryParameters.SelectedQueryName]: name,
          [Config.queryParameters.SelectedTraceId]: undefined,
          [Config.queryParameters.ActiveReportPaneTab]: newTabParam,
          [Config.queryParameters.SelectedTimeRange]: range,
          [Config.queryParameters.QueryListMetricSort]: metric,
        }),
      });
    },
    [history, location, tab, range],
  );

  if (loading) {
    return <OverviewLoading cards={overviewLoadingCards} />;
  }

  const currentUsageStats =
    data?.service?.currentStats?.queryStats?.[0]?.metrics;

  if (!data?.service) {
    return <OverviewLoading loadingError cards={overviewLoadingCards} />;
  }

  // Determine if this is a "no data" state using sparklines data. We can return
  // null since Service Report displays the NoData component
  const hasData =
    !!currentUsageStats &&
    data?.service?.sparklines?.queryStats?.some(
      (stat) => stat.metrics.totalLatencyHistogram.totalCount > 0,
    );
  if (!hasData) {
    return null;
  }

  const reportMinutes = date.resolutionToSeconds(resolution) / 60;
  const firstTotalMinutes = date.getMinDifference(timeFrom, timeTo);
  const secondTotalMinutes = date.getMinDifference(
    compareToTimeFrom,
    compareToTimeTo,
  );

  // Aggregate request and error stats from the sparklines data when using optimized query
  const currentMetrics = useOptimizedQuery
    ? aggregateStats(data?.service?.sparklines?.queryStats, firstTotalMinutes)
    : calculateMetrics(currentUsageStats, firstTotalMinutes);

  // The p95 can't be aggregated from sparklines, so get that from the
  // currentStats data
  if (useOptimizedQuery) {
    const currentP95Metrics = calculateMetrics(
      currentUsageStats,
      firstTotalMinutes,
    );
    currentMetrics.serviceTimeP95 = currentP95Metrics.serviceTimeP95;
  }

  let pastMetrics: NormalizedOperationsQueryStats | null = null;
  if (includeDeltaValues && data.service.pastStats?.queryStats?.[0]?.metrics) {
    pastMetrics = calculateMetrics(
      data.service.pastStats.queryStats[0].metrics,
      secondTotalMinutes,
    );
  }

  const topNOperationStats = getTopNOperationStats(useOptimizedQuery, data);

  const stats = data.service.sparklines?.queryStats || [];
  const sparklineMetrics: Record<string, number[]> = {};
  const min: Record<string, number> = {};
  const max: Record<string, number> = {};
  OVERVIEW_METRIC_KEYS.forEach((metric) => {
    sparklineMetrics[metric] = [];
  });

  stats.forEach((r) => {
    const usageStatMetrics = calculateMetrics(r.metrics, reportMinutes);
    OVERVIEW_METRIC_KEYS.forEach((metric) => {
      const metricVal = usageStatMetrics[metric];
      if (typeof metricVal === 'number')
        sparklineMetrics[metric]?.push(metricVal);
      // we choose to look at values above 0 in order to display statistics
      // that have meaning. This can mean that the error graph shows a single
      // figure range with a percentage increase from 0, which is acceptable,
      // since the range only takes into account queries with errors
      const minMetric = min[metric];
      const maxMetric = max[metric];
      if (
        typeof metricVal === 'number' &&
        metricVal > 0 &&
        (!minMetric || minMetric > metricVal)
      ) {
        min[metric] = metricVal;
      }
      if (
        typeof metricVal === 'number' &&
        metricVal > 0 &&
        (!maxMetric || maxMetric < metricVal)
      ) {
        max[metric] = metricVal;
      }
    });
  });

  return (
    <div>
      <Heading className="mb-4">Operations overview</Heading>
      <div className="grid w-full grid-cols-1 gap-6 @5xl:grid-cols-3">
        {OVERVIEW_METRIC_KEYS.map((metric) => (
          <OverviewCard
            key={metric}
            onQuerySelect={onQuerySelected}
            currentValue={currentMetrics[metric] || 0}
            pastValue={pastMetrics?.[metric] ?? null}
            sortKey={metric}
            min={min[metric] || 0}
            max={max[metric] || 0}
            sparklineData={sparklineMetrics[metric] ?? []}
            topOperations={topNOperationStats[metric]}
            timeRange={range}
          />
        ))}
      </div>
    </div>
  );
}
